import { type IntlShape } from "react-intl";
import { z } from "zod";

export const sizeSchema = z.object({
	width: z.number(),
	height: z.number(),
});
export type Size = z.infer<typeof sizeSchema>;

export const thumbnailResponseSchema = z.object({
	fileContents: z.string(),
});
export type ThumbnailResponse = z.infer<typeof thumbnailResponseSchema>;

export const printCartonSchema = z.object({
	prints: z.array(z.object({ value: z.string().optional() })).optional(),
});
export type PrintCarton = z.infer<typeof printCartonSchema>;

export const packagingSolutionSchema = z.object({
	id: z.string(),
	alias: z.string(),
	cartons: z.array(printCartonSchema),
});
export type PackagingSolution = z.infer<typeof packagingSolutionSchema>;

export const printImageDimensionsSchema = z
	.object({
		length: z.number(),
		width: z.number(),
		height: z.number(),
	})
	.nullable()
	.optional();

const storageLocationStrings = ["PackNet", "packnet", "sftp", "fiery"] as const;
export const storageLocationStringSchema = z.enum(storageLocationStrings);
export type StorageLocationString = z.infer<typeof storageLocationStringSchema>;

export const storageLocationSchema = z.union([z.number().int().min(0).max(2), storageLocationStringSchema]);
export type StorageLocation = z.infer<typeof storageLocationSchema>;

export const printImageSchema = z.object({
	id: z.string(),
	tenantId: z.string().optional(),

	alias: z.string(),
	imageId: z.string().optional(),
	thumbnailImageId: z.string().optional(),
	fileSize: z.number().optional(),
	fileName: z.string(),

	dimensions: printImageDimensionsSchema,
	ratio: z.union([z.number().nullable().optional(), printImageDimensionsSchema]),

	sftpFilePath: z.string().nullable().optional(),
	uploadDate: z.string().optional(),
	storageLocation: storageLocationSchema.optional(),
	productionGroups: z.array(z.string()).optional(),
	thumbnail: z.string().optional(),
	file: z.instanceof(File).nullable().optional(),
	imageURL: z.string().optional(),
	fileUrl: z.string().optional(),
	size: sizeSchema.optional(),
});

export const createPrintImageSchema = printImageSchema.omit({ id: true });

export const updatePrintImageSchema = printImageSchema.partial().required({ id: true });

const printImageDimensionMessages = {
	length: {
		label: "Length",
		number: { id: "Length must be a number", defaultMessage: "Length must be a number" },
		positive: { id: "Length must be greater than 0", defaultMessage: "Length must be greater than 0" },
		precision: {
			id: "Length must be a multiple of 0.0001",
			defaultMessage: "Length must be a multiple of 0.0001",
		},
	},
	width: {
		label: "Width",
		number: { id: "Width must be a number", defaultMessage: "Width must be a number" },
		positive: { id: "Width must be greater than 0", defaultMessage: "Width must be greater than 0" },
		precision: {
			id: "Width must be a multiple of 0.0001",
			defaultMessage: "Width must be a multiple of 0.0001",
		},
	},
	height: {
		label: "Height",
		number: { id: "Height must be a number", defaultMessage: "Height must be a number" },
		positive: { id: "Height must be greater than 0", defaultMessage: "Height must be greater than 0" },
		precision: {
			id: "Height must be a multiple of 0.0001",
			defaultMessage: "Height must be a multiple of 0.0001",
		},
	},
} as const;

const getPrintImageDimensionSchema = (intl: IntlShape, dimension: keyof typeof printImageDimensionMessages) => {
	const messages = printImageDimensionMessages[dimension];

	return z
		.number(intl.formatMessage(messages.number))
		.positive(intl.formatMessage(messages.positive))
		.multipleOf(0.0001, intl.formatMessage(messages.precision));
};

const getPrintImageFileSchema = (intl: IntlShape) =>
	z
		.instanceof(File, {
			message: intl.formatMessage({
				id: "A PDF file is required",
				defaultMessage: "A PDF file is required",
			}),
		})
		.refine((file) => file.type === "application/pdf", {
			message: intl.formatMessage({
				id: "Only PDF files are valid for upload",
				defaultMessage: "Only PDF files are valid for upload",
			}),
		});

export const getPrintImageConfigurationSchema = (intl: IntlShape, requireFile = false) => {
	const schema = z.object({
		file: getPrintImageFileSchema(intl).nullable().optional(),
		fileName: z
			.string(
				intl.formatMessage({
					id: "File name must be a string",
					defaultMessage: "File name must be a string",
				}),
			)
			.trim()
			.nonempty(
				intl.formatMessage({
					id: "File name is required",
					defaultMessage: "File name is required",
				}),
			),
		dimensions: z.object({
			length: getPrintImageDimensionSchema(intl, "length"),
			width: getPrintImageDimensionSchema(intl, "width"),
			height: getPrintImageDimensionSchema(intl, "height"),
		}),
	});

	if (requireFile === false) {
		return schema;
	}

	return schema.refine((data) => data.file !== null && data.file !== undefined, {
		path: ["file"],
		message: intl.formatMessage({
			id: "A PDF file is required",
			defaultMessage: "A PDF file is required",
		}),
	});
};

export type PrintImage = z.infer<typeof printImageSchema>;
export type PrintImageDimensions = z.infer<typeof printImageDimensionsSchema>;
export type CreatePrintImage = z.infer<typeof createPrintImageSchema>;
export type UpdatePrintImage = z.infer<typeof updatePrintImageSchema>;
export type PrintImageConfiguration = z.infer<ReturnType<typeof getPrintImageConfigurationSchema>>;
